import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import { Card } from '../../components/ui/card';
import axios from 'axios';
import { Package, Calendar, DollarSign, MapPin } from 'lucide-react';

const CustomerBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-foreground font-heading text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight mb-2">
            My Bookings
          </h1>
          <p className="text-muted-foreground">Track and manage your warehouse bookings</p>
        </div>

        {bookings.length === 0 ? (
          <Card className="bg-white border border-border rounded-sm shadow-sm p-12 text-center">
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
            <h3 className="font-heading text-xl font-bold text-foreground mb-2">No bookings yet</h3>
            <p className="text-muted-foreground">Start by searching for available warehouses</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4" data-testid="bookings-list">
            {bookings.map((booking) => (
              <Card key={booking.id} data-testid={`booking-${booking.id}`} className="bg-white border border-border rounded-sm shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-xl font-bold text-foreground mb-1">
                      Booking #{booking.id.slice(0, 8).toUpperCase()}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" strokeWidth={1.5} />
                        <span>{new Date(booking.booking_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground mb-1">Total Amount</p>
                    <p className="font-bold text-2xl text-foreground">${booking.total_amount.toFixed(2)}</p>
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide mt-2 ${
                      booking.status === 'pending' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                      booking.status === 'active' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                      'bg-slate-100 text-slate-600 border border-slate-200'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-3">Booking Items</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {booking.items.map((item, index) => (
                      <div key={index} className="p-3 bg-secondary rounded-sm">
                        <p className="text-sm font-medium text-foreground capitalize">
                          {item.item_type.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Qty: {item.quantity} {item.unit_type.toUpperCase()}
                        </p>
                        <p className="text-sm font-bold text-foreground mt-1">
                          ${item.price.toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerBookings;